/**
 * Pure helpers extracted from StorefrontSavedPaymentMethods.vue so they can be
 * unit-tested under vitest (which can't mount Vuetify 3 SFCs in this project —
 * see project_vitest-no-plugin-vue.md).
 *
 * Backend contract (PR #1001 / api/src/routes/stripe.py):
 *   - POST /stripe/listPaymentMethods → { payment_methods: [{id, brand,
 *     last4, exp_month, exp_year, is_default}, ...] } (account-scoped)
 *   - POST /stripe/createSetupIntent → { client_secret, setup_intent_id }
 *   - POST /stripe/detachPaymentMethod {payment_method_id} → { success: true }
 *   - POST /stripe/setDefaultPaymentMethod {payment_method_id} → sets default
 *   - setup_intent.succeeded webhook auto-sets first card as default
 */

// Lowercase brand → human-friendly label.
const BRAND_LABELS = {
  visa: "Visa",
  mastercard: "Mastercard",
  amex: "Amex",
  "american express": "Amex",
  discover: "Discover",
  diners: "Diners",
  jcb: "JCB",
  unionpay: "UnionPay",
};

// Lowercase brand → Material icon name. We use Material's generic "credit_card"
// for everything since Material doesn't ship brand-specific marks; the brand
// label + last4 carry the recognition. Kept as a mapping so we can swap in a
// brand-specific icon set later without touching the component.
const BRAND_ICONS = {
  visa: "credit_card",
  mastercard: "credit_card",
  amex: "credit_card",
  "american express": "credit_card",
  discover: "credit_card",
  diners: "credit_card",
  jcb: "credit_card",
  unionpay: "credit_card",
};

/**
 * Format a payment method into display fields for list rendering.
 *
 *   { brand: "Visa", masked: "•••• 4242", expiry: "12/29", isDefault: bool }
 *
 * Returns empty/false defaults for null/undefined input so callers can use
 * the result unconditionally in templates.
 *
 * @param {object|null|undefined} pm
 * @returns {{brand: string, masked: string, expiry: string, isDefault: boolean}}
 */
export function formatCardDisplay(pm) {
  if (!pm || typeof pm !== "object") {
    return { brand: "", masked: "", expiry: "", isDefault: false };
  }
  const rawBrand =
    typeof pm.brand === "string" ? pm.brand.toLowerCase() : "";
  const brand =
    BRAND_LABELS[rawBrand] ||
    (rawBrand ? rawBrand.charAt(0).toUpperCase() + rawBrand.slice(1) : "");
  const masked = pm.last4 ? `•••• ${pm.last4}` : "";
  const expiry = formatExpiry(pm.exp_month, pm.exp_year);
  return {
    brand,
    masked,
    expiry,
    isDefault: !!pm.is_default,
  };
}

function formatExpiry(month, year) {
  if (month == null || year == null) return "";
  const m = String(month).padStart(2, "0");
  const yStr = String(year);
  const yy = yStr.length >= 2 ? yStr.slice(-2) : yStr.padStart(2, "0");
  return `${m}/${yy}`;
}

/**
 * Sort payment methods with the default first. Non-default cards retain their
 * original order (Stripe's listing order, which is created-desc).
 *
 * Does NOT mutate the input.
 *
 * @param {Array|null|undefined} pms
 * @returns {Array}
 */
export function sortPmsDefaultFirst(pms) {
  if (!Array.isArray(pms)) return [];
  const list = pms.slice();
  return list
    .map((pm, index) => ({ pm, index }))
    .sort((a, b) => {
      const aDefault = !!(a.pm && a.pm.is_default);
      const bDefault = !!(b.pm && b.pm.is_default);
      if (aDefault && !bDefault) return -1;
      if (!aDefault && bDefault) return 1;
      return a.index - b.index;
    })
    .map((wrapped) => wrapped.pm);
}

/**
 * Return a Material icon name for a Stripe card brand. Falls back to
 * "credit_card" for unknown / null / undefined brands.
 *
 * @param {string|null|undefined} brand
 * @returns {string}
 */
export function brandIconName(brand) {
  if (typeof brand !== "string") return "credit_card";
  const key = brand.toLowerCase();
  return BRAND_ICONS[key] || "credit_card";
}

/**
 * Sanity-check a /stripe/createSetupIntent response shape before we hand it
 * to Stripe Elements. Throws an Error if either field is missing/empty.
 *
 * @param {object|null|undefined} response
 * @returns {true} on success
 * @throws {Error} on invalid response
 */
export function validateSetupIntentResponse(response) {
  if (!response || typeof response !== "object") {
    throw new Error("Invalid setup intent response: response is empty");
  }
  if (typeof response.client_secret !== "string" || !response.client_secret) {
    throw new Error("Invalid setup intent response: missing client_secret");
  }
  if (
    typeof response.setup_intent_id !== "string" ||
    !response.setup_intent_id
  ) {
    throw new Error("Invalid setup intent response: missing setup_intent_id");
  }
  return true;
}
